import { useCallback, type ComponentProps, type FocusEvent, type KeyboardEvent, type MouseEvent } from "react";
import { cn } from "@/lib/utils";
import { queueSelectInputText, selectInputText } from "./inputFocus";
import { parseNumericInput } from "./numberFormatting";
import { SCRUB_FIELD_INPUT_CLASS } from "./scrubFieldStyles";

export function ScrubInput({ className, type, onFocus, onMouseDown, onKeyDown, onClick, ...props }: ComponentProps<"input">) {
  const dataNumeric = (props as Record<string, unknown>)["data-numeric"];
  const isNumeric = type === "number" || dataNumeric === true || dataNumeric === "true";

  const handleFocus = useCallback((event: FocusEvent<HTMLInputElement>) => {
    onFocus?.(event);
    queueSelectInputText(event.currentTarget);
  }, [onFocus]);

  const handleMouseDown = useCallback((event: MouseEvent<HTMLInputElement>) => {
    onMouseDown?.(event);
    if (event.defaultPrevented || event.button !== 0 || props.disabled) {
      return;
    }

    const input = event.currentTarget;
    if (document.activeElement !== input) {
      return;
    }

    event.preventDefault();
    selectInputText(input);
  }, [onMouseDown, props.disabled]);

  const handleClick = useCallback((event: MouseEvent<HTMLInputElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || props.disabled) {
      return;
    }

    selectInputText(event.currentTarget);
  }, [onClick, props.disabled]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || !isNumeric || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) {
      return;
    }

    event.preventDefault();
    incrementNumericInput(event.currentTarget, event.key === "ArrowUp" ? 1 : -1);
    queueSelectInputText(event.currentTarget);
  }, [isNumeric, onKeyDown]);

  return (
    <input
      type={type}
      data-slot="scrub-input"
      className={cn(SCRUB_FIELD_INPUT_CLASS, className)}
      {...props}
      onFocus={handleFocus}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    />
  );
}

function incrementNumericInput(input: HTMLInputElement, direction: number): void {
  const current = parseNumericInput(input.value);
  const stepAttr = Number.parseFloat(input.step);
  const step = Number.isFinite(stepAttr) && stepAttr > 0 ? stepAttr : 1;
  const fallback = direction > 0 ? 0 : step;
  const next = clampToInputRange(
    (current !== undefined ? current : fallback) + direction * step,
  );
  setNativeInputValue(input, formatNumericValue(next));
  input.dispatchEvent(new Event("input", { bubbles: true }));

  function clampToInputRange(value: number): number {
    const min = Number.parseFloat(input.min);
    const max = Number.parseFloat(input.max);
    const withMin = Number.isFinite(min) ? Math.max(min, value) : value;
    return Number.isFinite(max) ? Math.min(max, withMin) : withMin;
  }
}

function setNativeInputValue(input: HTMLInputElement, value: string): void {
  const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value");
  descriptor?.set?.call(input, value);
}

function formatNumericValue(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(6)));
}
